import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddAltSharpIcon from "@mui/icons-material/PersonAddAltSharp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useSubmissionStore } from "../../../../models/applications/SubmissionStore";
import MDButton from "../../../../components/MDButton";
import { get, post } from "requests";
import UserAvatar from "./UserAvatar";
import Tooltip from "@mui/material/Tooltip";

interface AssignedUser {
  fullName: string;
  firstName: string;
  lastName: string;
  role: string;
  color: string;
  userId?: number;
}

interface UsersRoleProps {
  appId: number;
  assignedUsers: AssignedUser[];
  setAssignedUsers: React.Dispatch<React.SetStateAction<AssignedUser[]>>;
}

type Role = "ISO Representative" | "Senior Underwriter" | "Junior Underwriter" | "DELETE ROLE";

const UsersRole: React.FC<UsersRoleProps> = ({ appId, assignedUsers, setAssignedUsers }) => {
  const { userList, dealName } = useSubmissionStore();
  const [open, setOpen] = useState(false);
  const [assignableRoles, setAssignableRoles] = useState<string[]>([]);
  const [editableRoles, setEditableRoles] = useState<string[]>([]);

  const [selectedUser, setSelectedUser] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const fetchAssignableRoles = async () => {
      try {
        const roles = await get("getAssignableRoles");
        setAssignableRoles(roles);
        setEditableRoles([...roles, "DELETE ROLE"]);
      } catch (error) {
        console.error("Error fetching assignable roles:", error);
      }
    };

    fetchAssignableRoles();
  }, []);

  useEffect(() => {
    const fetchUsersRole = async () => {
      try {
        const applicationId = appId;

        const usersRoleResponse = await get(`getUsersRole/${applicationId}`);
        const updatedAssignedUsers: AssignedUser[] = [];

        Object.entries(usersRoleResponse).forEach(([role, userNames]) => {
          (userNames as string[]).forEach((fullName) => {
            const [firstName, ...lastNameArr] = fullName.split(" ");
            const lastName = lastNameArr.join(" ");
            const user = userList?.find((u) => u.fullName === fullName);

            if (user) {
              updatedAssignedUsers.push({
                fullName,
                firstName,
                lastName,
                role,
                color: getRandomColor(firstName),
                userId: user.id,
              });
            }
          });
        });
        setAssignedUsers(updatedAssignedUsers);
      } catch (error) {
        console.error("Error fetching usersRole:", error);
      }
    };
    fetchUsersRole();
  }, [appId, userList]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser("");
    setSelectedRole("");
    setErrorMessage("");
    setShowError(false);
  };

  const handleUserChange = (value: any) => {
    setSelectedUser(value?.fullName || "");
  };

  const handleRoleChange = async (index: number, newRole: string) => {
    const user = assignedUsers[index];
    if (newRole === "DELETE ROLE") {
      const success = await deleteUserRole(appId, user?.userId!, user?.role);
      if (success) {
        const updatedAssignedUsers = assignedUsers.filter((_, i) => i !== index);
        setAssignedUsers(updatedAssignedUsers);
      }
    } else {
      const updatedUsers = [...assignedUsers];
      updatedUsers[index].role = newRole;
      setAssignedUsers(updatedUsers);

      if (user.userId) {
        await assignUserRole(newRole as string, appId, user.userId);
      } else {
        console.error("User ID is missing for:", user.fullName);
      }
    }
  };

  const handleNewRoleChange = (event: SelectChangeEvent<string>) => {
    setSelectedRole(event.target.value);
  };

  const assignUserRole = async (role: string, applicationId: number, userId: number) => {
    try {
      let endpoint;
      switch (role) {
        case "ISO Representative":
          endpoint = "setRepresentativeUser";
          break;
        case "Senior Underwriter":
          endpoint = "setSeniorUnderwriter";
          break;
        case "Junior Underwriter":
          endpoint = "setJuniorUnderwriter";
          break;
        default:
          endpoint = "setUserRoles";
      }

      if (endpoint === "setUserRoles") {
        const response = await post(endpoint, { applicationId, userId, roleName: role });
        console.log(`${role} Set:`, response.data);
      } else {
        const response = await post(endpoint, { applicationId, userId });
        console.log(`${role} Set:`, response.data);
      }
    } catch (error) {
      console.error(`Error setting ${role}:`, error);
    }
  };

  const setRepresentativeUser = async (applicationId: number, userId: number) => {
    try {
      const response = await post("setRepresentativeUser", {
        applicationId,
        userId,
      });
      console.log("Representative User Set:", response.data);
    } catch (error) {
      console.error("Error setting Representative User:", error);
    }
  };

  const setJuniorUnderwriter = async (applicationId: number, userId: number) => {
    try {
      const response = await post("setJuniorUnderwriter", {
        applicationId,
        userId,
      });
      console.log("Junior Underwriter Set:", response.data);
    } catch (error) {
      console.error("Error setting Junior Underwriter:", error);
    }
  };

  const setSeniorUnderwriter = async (applicationId: number, userId: number) => {
    try {
      const response = await post("setSeniorUnderwriter", {
        applicationId,
        userId,
      });
      console.log("Senior Underwriter Set:", response.data);
    } catch (error) {
      console.error("Error setting Senior Underwriter:", error);
    }
  };

  const deleteUserRole = async (applicationId: number, userId: number, roleType: string) => {
    try {
      const response = await post("/delete/userRoles", { applicationId, userId, roleType });
      console.log("User Role Deleted:", response.data);
      return true;
    } catch (error) {
      console.error("Error deleting User Role:", error);
      return false;
    }
  };

  const assignRole = async () => {
    if (!selectedUser || !selectedRole) {
      setErrorMessage("Please select a user and a role.");
      setShowError(true);
      return;
    }
    const user = userList?.find((user) => user.fullName === selectedUser);
    if (user) {
      const newAssignedUser = {
        fullName: user.fullName,
        firstName: user.firstName,
        lastName: user.lastName,
        role: selectedRole,
        color: getRandomColor(user.firstName),
        userId: user.id,
      };

      const updatedAssignedUsers = [...assignedUsers, newAssignedUser];
      setAssignedUsers(updatedAssignedUsers);
      try {
        switch (selectedRole) {
          case "ISO Representative":
            await setRepresentativeUser(appId, user.id);
            break;
          case "Junior Underwriter":
            await setJuniorUnderwriter(appId, user.id);
            break;
          case "Senior Underwriter":
            await setSeniorUnderwriter(appId, user.id);
            break;
          default:
            await post("setUserRoles", {
              applicationId: appId,
              userId: user.id,
              roleName: selectedRole,
            });
        }
        setSelectedUser("");
        setSelectedRole("");
      } catch (error) {
        console.error("Error assigning role:", error);
      }
    }
  };

  const getRandomColor = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash % 360);

    const saturation = 80;
    const lightness = 40;

    console.log(`name ${name} : hsl(${hue}, ${saturation}%, ${lightness}%)`);

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  return (
    <div>
      <IconButton onClick={handleClickOpen}>
        <Tooltip title="Click to add users" arrow>
          <Avatar sx={{ backgroundColor: "#2c2c2c", width: 38, height: 38, marginLeft: "8px" }}>
            <PersonAddAltSharpIcon
              sx={{ width: 28, height: 28, color: "white", marginLeft: "4px" }}
            />
          </Avatar>
        </Tooltip>
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "600px",
          },
        }}
      >
        <DialogTitle>Assigned Users</DialogTitle>
        <DialogContent>
          {/* Assigned Users List */}
          <Box display="flex" flexDirection="column" gap={1} marginBottom={2}>
            {assignedUsers.map((user, index) => (
              <Box key={index} display="flex" alignItems="center" gap={3} paddingX={1}>
                <UserAvatar
                  firstName={user.firstName}
                  lastName={user.lastName}
                  color={user.color}
                />
                <Box
                  sx={{
                    width: "150px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "15px",
                      fontFamily: "'Roboto', sans-serif",
                      color: "#333",
                      marginTop: "8px",
                      marginLeft: "6px",
                    }}
                  >
                    {user.fullName}
                  </Typography>
                </Box>

                <Select
                  value={user.role}
                  onChange={(event) => handleRoleChange(index, event.target.value as Role)}
                  displayEmpty
                  IconComponent={(props) => <KeyboardArrowDownIcon />}
                  sx={{
                    fontSize: "15px",
                    fontFamily: "'Roboto', sans-serif",
                    color: "#333",
                    marginTop: "8px",
                    marginLeft: "55px",
                    width: "200px",
                    borderBottom: "2px solid #ccc",
                    paddingBottom: "4px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                >
                  {editableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ))}
          </Box>

          {/* Add User Section */}
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ backgroundColor: "#2c2c2c", width: 39, height: 39, marginLeft: "8px" }}>
              <PersonAddAltSharpIcon
                sx={{ width: 29, height: 29, color: "white", marginLeft: "4px" }}
              />
            </Avatar>
            <Autocomplete
              options={userList.filter((usr) => usr?.type?.toLowerCase() === "internal")}
              getOptionLabel={(option) => option.fullName || ""}
              value={userList.find((u) => u.fullName === selectedUser) || null}
              onChange={(event, newValue) => {
                handleUserChange(newValue);
              }}
              popupIcon={<KeyboardArrowDownIcon />}
              filterOptions={(options, { inputValue }) =>
                options.filter((option) =>
                  option.fullName?.toLowerCase().includes(inputValue.toLowerCase())
                )
              }
              isOptionEqualToValue={(option, value) => option.fullName === value.fullName}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="Choose a User"
                  placeholder="Choose a User"
                  sx={{
                    height: 35,
                    width: "250px",
                    minWidth: "150px",
                    maxWidth: "300px",
                    marginRight: "30px",
                    fontStyle: "italic",
                  }}
                />
              )}
            />

            <Select
              value={selectedRole}
              onChange={handleNewRoleChange}
              displayEmpty
              IconComponent={() => <KeyboardArrowDownIcon />}
              sx={{
                height: 40,
                width: "200px",
                minWidth: "150px",
                maxWidth: "300px",
                fontStyle: "italic",
                borderBottom: "1px solid #ccc",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              <MenuItem value="" disabled>
                Job Role
              </MenuItem>
              {assignableRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {showError && (
            <Typography
              sx={{
                color: "error.main",
                fontSize: "0.875rem",
                marginTop: "15px",
                marginLeft: "20px",
              }}
            >
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <MDButton
              onClick={assignRole}
              variant="contained"
              color="primary"
              size="small"
              sx={{ padding: "4px 12px", fontSize: "0.75rem", marginRight: "10px" }}
            >
              Assign Role
            </MDButton>
            <MDButton
              onClick={handleClose}
              variant="contained"
              color="primary"
              size="small"
              sx={{ padding: "4px 12px", fontSize: "0.75rem", marginRight: "10px" }}
            >
              Close
            </MDButton>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UsersRole;
